/** 来源：market Table；补入包级空态与可选行点击（onRowClick），加载骨架仍归 DataTable。 */
import type { KeyboardEvent, ReactNode } from 'react'

export interface TableColumn<T> {
  key: string
  title: ReactNode
  align?: 'left' | 'right' | 'center'
  render: (row: T) => ReactNode
  className?: string
  /** 桌面端（≥md）单元格最小宽度（auto 布局下 width 只是建议值，会被压） (LeapWorldTwin-233) */
  minWidth?: string
  /** 长文本列显式声明可换行；默认桌面端单元格不换行（md:whitespace-nowrap） (LeapWorldTwin-233) */
  wrap?: boolean
}

const alignClass = { left: 'text-left', right: 'text-right', center: 'text-center' } as const

export function Table<T>({ columns, rows, rowKey, empty = '暂无数据', ariaLabel, onRowClick }: {
  columns: TableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string | number
  empty?: ReactNode
  ariaLabel?: string
  /** 传了即整行可点（鼠标 + Enter/空格），与 DataTable 的可点行行为一致 */
  onRowClick?: (row: T) => void
}) {
  const onKeyDown = (row: T) => (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      onRowClick(row)
    }
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm" aria-label={ariaLabel}>
        <thead>
          <tr className="border-b border-[var(--color-line)] text-xs text-[var(--color-faint)]">
            {columns.map((column) => (
              <th key={column.key} style={column.minWidth ? { minWidth: column.minWidth } : undefined} className={`whitespace-nowrap px-3 py-2 font-medium first:pl-5 last:pr-5 ${alignClass[column.align ?? 'left']}`}>
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={Math.max(1, columns.length)} className="px-5 py-8 text-center text-sm text-[var(--color-muted)]">
                {empty}
              </td>
            </tr>
          ) : rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={`border-b border-[var(--color-line)] transition-colors last:border-b-0 hover:bg-[var(--color-soft)]${onRowClick ? ' cursor-pointer focus-visible:bg-[var(--color-soft)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]' : ''}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? onKeyDown(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                  className={`max-md:min-w-24 px-3 py-2.5 first:pl-5 last:pr-5 ${column.wrap ? '' : 'md:whitespace-nowrap'} ${alignClass[column.align ?? 'left']} ${column.className ?? ''}`.trim()}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
